/**
 * CallTrackingMetrics Error Parser Module (Story 22.3, AC #5, AC #7)
 *
 * HTTP-status-first error handling (carry-forward from CallRail 22.1).
 * CTM does NOT document structured error codes beyond the HTTP status
 * — per the [official CTM Postman collection](https://postman.calltrackingmetrics.com/)
 * observed 2026-05-15 and the [GitHub PHP SDK error handling](https://github.com/calltracking/ctm-php)
 * verified 2026-05-15, the response body's shape is described only as
 * a JSON envelope with optional `error` / `message` fields. The parser
 * defensively extracts a `message` or `error` field if present, but
 * tolerates absence.
 *
 *   - HTTP 400 → `validation_error` retryable=false (malformed request
 *     payload). **EXCEPT** the literal `"per_page limit is 150"` message
 *     — adapter treats this as a guard-misconfiguration internal error
 *     (the helper should have clamped client-side; surfaces a developer-
 *     facing alert in Sentry but a generic user message).
 *   - HTTP 401 → `token_expired` retryable=false (keys are invalid or
 *     revoked).
 *   - HTTP 403 → `permission_denied` retryable=false (plan downgrade or
 *     user permission removal; if response body mentions "subscription"
 *     or "plan" or "upgrade", caller surfaces the upgrade prompt).
 *   - HTTP 404 → `account_not_found` (rare since helpers hardcode paths
 *     — usually means `account_id` not in the discovered allowlist).
 *   - HTTP 422 → `validation_error` retryable=false (treat as field-
 *     not-allowed-for-this-subscription per the catalogue line 9 — CI /
 *     VoiceAI / Scorecards entitlement gate).
 *   - HTTP 429 → `rate_limited` retryable=true. **No documented
 *     `Retry-After` header** (verified web search 2026-05-15) — the
 *     adapter falls back to a 60-second cooldown via the surrounding
 *     queue retry policy.
 *   - HTTP 5xx → `provider_unavailable` retryable=true with exponential
 *     backoff.
 *
 * **Sanitization (Round 3 Red Team Attack #3):** error messages MUST
 * flow through `sanitizeCtmErrorMessage` before reaching any log line
 * or `AdapterError.message`. CTM sends both secrets via the
 * `Authorization: Basic <base64>` header — the base64 form, the
 * plaintext `ACCESS_KEY:SECRET_KEY` form, AND each individual secret
 * value can leak into error message echoes (if CTM echoes back what we
 * sent in a malformed-request error). The sanitizer redacts ALL of
 * these surfaces.
 */

import type { AdapterErrorCode } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";

/**
 * Closed enumeration of CTM error types. Mirrors the CallRail 22.1
 * shape for cross-adapter consistency.
 */
export type CtmErrorType =
  | "unauthorized" // 401 — invalid or revoked credential pair
  | "forbidden" // 403 — plan downgrade / permission removal / subscription gate
  | "not_found" // 404 — endpoint hardcoded incorrectly OR account_id outside allowlist
  | "validation_error" // 400 / 422 — malformed request payload or CI-gated field on non-CI plan
  | "rate_limit_exceeded" // 429 — ~300 req/min/account soft cap exceeded
  | "provider_unavailable" // 5xx — CTM regional outage
  | "per_page_misconfig" // 400 with literal "per_page limit is 150" — internal guard miss
  | "unknown";

export interface CtmApiError {
  readonly type: CtmErrorType;
  readonly message: string;
  readonly status: number | null;
  readonly retryable: boolean;
  /** Optional `message` / `error` field extracted from the response body if present. */
  readonly providerMessage?: string;
}

// ============================================================================
// Status fallback map
// ============================================================================

const STATUS_TO_TYPE: Record<
  number,
  { type: CtmErrorType; retryable: boolean }
> = {
  400: { type: "validation_error", retryable: false },
  401: { type: "unauthorized", retryable: false },
  403: { type: "forbidden", retryable: false },
  404: { type: "not_found", retryable: false },
  405: { type: "validation_error", retryable: false },
  406: { type: "validation_error", retryable: false },
  422: { type: "validation_error", retryable: false },
  429: { type: "rate_limit_exceeded", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

const TYPE_TO_ADAPTER_CODE: Record<CtmErrorType, AdapterErrorCode> = {
  unauthorized: "token_expired",
  forbidden: "permission_denied",
  not_found: "account_not_found",
  validation_error: "invalid_request",
  rate_limit_exceeded: "rate_limited",
  provider_unavailable: "provider_unavailable",
  per_page_misconfig: "invalid_request",
  unknown: "invalid_response",
};

function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

/**
 * The literal error message CTM returns when `per_page > 150` per the
 * catalogue line 9 + Postman observations 2026-05-15. Used by the
 * parser to upgrade a generic 400 to a more diagnostic `per_page_misconfig`
 * type so the developer alert can fire with the right context.
 */
export const CTM_PER_PAGE_ERROR_MESSAGE = "per_page limit is 150";

// ============================================================================
// Sanitization (Round 3 Red Team Attack #3)
// ============================================================================

/**
 * Wraps the generic `sanitizeErrorMessage` helper with CTM-specific
 * defenses against dual-secret leaks:
 *
 *  1. Replace every occurrence of the Base64-encoded
 *     `Basic <base64(ACCESS_KEY:SECRET_KEY)>` form with
 *     `Basic [redacted-ctm-base64]`. Defends against the case where
 *     the request `Authorization` header leaks into a fetch error
 *     message (e.g. "got 401 with Authorization: Basic abc123= header").
 *  2. Replace the plaintext `ACCESS_KEY:SECRET_KEY` form (the value
 *     before Base64 encoding) with `[redacted-ctm-access]:[redacted-ctm-secret]`
 *     — defensive against proxy logs that echo URL-encoded
 *     auth payloads or against the customer's secret leaking via an
 *     adversarial error string echo.
 *  3. Replace every literal occurrence of the individual ACCESS_KEY or
 *     SECRET_KEY value with `[redacted-ctm-token]`. Real CTM keys are
 *     30-50 alphanumeric chars per the Postman collection observed
 *     2026-05-15; a 20-char threshold (matching the Zod schema minimum)
 *     prevents false-positives on legitimate prose substrings during
 *     dev-mode testing.
 *  4. Replace URL-encoded variants of the Basic-auth header
 *     (`Basic+...`, `Basic%20...`) — defensive against debug
 *     endpoints or proxies that URL-encode header values in forensic
 *     logs.
 *
 * Multiline-aware so multiple headers / echoes on separate lines all
 * get caught. Bounded character classes prevent catastrophic backtracking
 * on adversarial inputs.
 */
export function sanitizeCtmErrorMessage(
  message: string,
  accessKey?: string,
  secretKey?: string,
): string {
  // Run the generic sanitizer FIRST — it strips Bearer tokens, long
  // base64 strings, generic OAuth-shape secrets.
  let sanitized = sanitizeErrorMessage(message);

  // (1) Replace `Basic <base64-payload>` header pattern. Bounded
  // `[A-Za-z0-9+/=]{16,4096}` so adversarial inputs cannot trigger
  // backtracking; the Base64 payload runs to the next non-Base64
  // character. Case-insensitive in case CTM echoes back lowercase
  // variants.
  sanitized = sanitized.replace(
    /Basic\s+[A-Za-z0-9+/=]{16,4096}/gi,
    "Basic [redacted-ctm-base64]",
  );

  // (2) URL-encoded variants: `Basic+<payload>` (form-encoding) or
  // `Basic%20<payload>` (percent-encoding). Defensive — CTM uses
  // headers, not query params, but a future debug endpoint or proxy
  // might URL-encode.
  sanitized = sanitized.replace(
    /Basic[+%]20?[A-Za-z0-9+/=%]{16,4096}/gi,
    "Basic [redacted-ctm-base64]",
  );

  // (3) Plaintext `ACCESS_KEY:SECRET_KEY` form. Only fires when BOTH
  // are provided (otherwise we can't construct the literal). Threshold
  // 20 chars per key matches the Zod connect-schema lower bound.
  if (
    accessKey &&
    accessKey.length >= 20 &&
    secretKey &&
    secretKey.length >= 20
  ) {
    const escAccess = accessKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escSecret = secretKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(
      new RegExp(`${escAccess}:${escSecret}`, "g"),
      "[redacted-ctm-access]:[redacted-ctm-secret]",
    );
  }

  // (4) Exact individual-secret replacement — defends against the
  // case where the decrypted plaintext key leaked into a non-header
  // position (e.g. the provider echoed the key value back in a
  // malformed-request error body).
  if (accessKey && accessKey.length >= 20) {
    const escaped = accessKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(
      new RegExp(escaped, "g"),
      "[redacted-ctm-token]",
    );
    // URL-encoded variant of the raw key value (rare for alphanumeric
    // keys, but the CTM Postman collection observed 2026-05-15
    // sometimes encodes underscores as %5F in forensic logs).
    const urlEncoded = encodeURIComponent(accessKey);
    if (urlEncoded !== accessKey) {
      const escapedEncoded = urlEncoded.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      sanitized = sanitized.replace(
        new RegExp(escapedEncoded, "g"),
        "[redacted-ctm-token]",
      );
    }
  }
  if (secretKey && secretKey.length >= 20) {
    const escaped = secretKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(
      new RegExp(escaped, "g"),
      "[redacted-ctm-token]",
    );
    const urlEncoded = encodeURIComponent(secretKey);
    if (urlEncoded !== secretKey) {
      const escapedEncoded = urlEncoded.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      sanitized = sanitized.replace(
        new RegExp(escapedEncoded, "g"),
        "[redacted-ctm-token]",
      );
    }
  }

  return sanitized;
}

// ============================================================================
// HTTP-status-first parser (AC #7)
// ============================================================================

/**
 * Parse an HTTP error response from CTM.
 *
 * CTM does NOT document a structured error response body shape beyond
 * the JSON envelope. The parser defensively extracts a `message`,
 * `error`, `errors`, or `detail` field from the JSON body if present,
 * but falls back gracefully when the body is non-JSON or lacks those
 * fields.
 *
 * **Special case 400 with `"per_page limit is 150"`:** upgraded from
 * generic `validation_error` to the diagnostic `per_page_misconfig`
 * type so the developer alert can surface the right context. The
 * client-side `Math.min(perPage, 150)` clamp at the helper level
 * should prevent this from ever firing in production; if it does, it
 * means a helper builder leaked a non-clamped value.
 *
 * @param httpStatus - HTTP status code from the response.
 * @param body - Optional raw response body for forensic message context.
 * @param accessKey - Optional plaintext Access Key for sanitization.
 * @param secretKey - Optional plaintext Secret Key for sanitization.
 */
export function parseCtmApiError(
  httpStatus: number,
  body?: string,
  accessKey?: string,
  secretKey?: string,
): CtmApiError {
  // Defensive JSON parse — if body is present and parseable, extract
  // common error-message field names. We do NOT throw on parse failure
  // (CTM's docs are silent on the response body schema, so we tolerate
  // any shape).
  let providerMessage: string | undefined;
  if (body && body.trim().length > 0) {
    try {
      const parsed = JSON.parse(body) as Record<string, unknown>;
      const candidates = [
        parsed["message"],
        parsed["error"],
        parsed["errors"],
        parsed["detail"],
      ];
      for (const c of candidates) {
        if (typeof c === "string" && c.length > 0) {
          providerMessage = c;
          break;
        }
        if (Array.isArray(c) && c.length > 0 && typeof c[0] === "string") {
          providerMessage = c.join("; ");
          break;
        }
        // Rails-typical validation-error shape from `ctm-php` (which
        // wraps a Rails backend per github.com/calltracking/ctm-php):
        // `errors: { field: ["msg1", "msg2"] }`. Flatten to
        // `"field: msg1, msg2"`.
        if (c !== null && typeof c === "object" && !Array.isArray(c)) {
          const parts: string[] = [];
          for (const [field, val] of Object.entries(c as Record<string, unknown>)) {
            if (Array.isArray(val)) {
              const strings = val.filter((v): v is string => typeof v === "string");
              if (strings.length > 0) parts.push(`${field}: ${strings.join(", ")}`);
            } else if (typeof val === "string" && val.length > 0) {
              parts.push(`${field}: ${val}`);
            }
          }
          if (parts.length > 0) {
            providerMessage = parts.join("; ");
            break;
          }
        }
      }
    } catch {
      // Body isn't JSON — fall through to raw-body inclusion below.
    }
  }

  // Special-case the per_page=150 ceiling violation.
  if (
    httpStatus === 400 &&
    providerMessage &&
    providerMessage.toLowerCase().includes(CTM_PER_PAGE_ERROR_MESSAGE)
  ) {
    const baseMessage = `CTM API error: HTTP 400 — per_page guard miss (helper should clamp client-side to ${150}). Provider message: ${providerMessage}`;
    return {
      type: "per_page_misconfig",
      message: sanitizeCtmErrorMessage(baseMessage, accessKey, secretKey),
      status: 400,
      retryable: false,
      providerMessage: sanitizeCtmErrorMessage(
        providerMessage,
        accessKey,
        secretKey,
      ),
    };
  }

  const mapping = STATUS_TO_TYPE[httpStatus];
  const baseMessage = `CTM API error: HTTP ${httpStatus}${
    providerMessage ? ` — ${providerMessage}` : body ? ` — ${body.slice(0, 500)}` : ""
  }`;
  const sanitized = sanitizeCtmErrorMessage(baseMessage, accessKey, secretKey);

  if (mapping) {
    return {
      type: mapping.type,
      message: sanitized,
      status: httpStatus,
      retryable: mapping.retryable,
      providerMessage: providerMessage
        ? sanitizeCtmErrorMessage(providerMessage, accessKey, secretKey)
        : undefined,
    };
  }

  return {
    type: "unknown",
    message: sanitized,
    status: httpStatus,
    retryable: isRetryableStatus(httpStatus),
    providerMessage: providerMessage
      ? sanitizeCtmErrorMessage(providerMessage, accessKey, secretKey)
      : undefined,
  };
}

/**
 * Map a `CtmErrorType` to the canonical `AdapterErrorCode` used by the
 * adapter contract.
 */
export function ctmErrorTypeToAdapterCode(type: CtmErrorType): AdapterErrorCode {
  return TYPE_TO_ADAPTER_CODE[type];
}

// ============================================================================
// Throwable wrapper
// ============================================================================

/**
 * Throwable error class used by REST helpers to surface a parsed CTM
 * error to the adapter orchestrator. Mirrors `CallRailApiCallError` /
 * `MozApiCallError` shape so the adapter dispatch logic stays uniform.
 */
export class CtmApiCallError extends Error {
  readonly status: number;
  readonly type: CtmErrorType;
  readonly retryable: boolean;
  readonly providerMessage?: string;

  constructor(parsed: CtmApiError) {
    super(parsed.message);
    this.name = "CtmApiCallError";
    this.status = parsed.status ?? 0;
    this.type = parsed.type;
    this.retryable = parsed.retryable;
    this.providerMessage = parsed.providerMessage;
  }
}
